import { ref, type Ref } from 'vue';
import { type DBRecord, PreferencesDB } from '../services/PreferencesDB.ts';
import { restoreOrBackup } from '../services/FirebaseDB.ts';
import { Profile } from '../models/Profile.ts';
import { doc, getFirestore, updateDoc } from 'firebase/firestore';
import { useCurrentUser } from 'vuefire';

type Constructor<T> = { new (): T };

const liveCollections = new Map<string, Ref<DBRecord[]>>();
const liveItems = new Map<string, Ref<DBRecord | undefined>>();

function getItemKey(namespace: string, id: string) {
  return `${namespace}:${id}`;
}

export function usePreferencesCollection<T extends DBRecord>(
  type: Constructor<T>,
  sortNewestFirst = false
): Ref<T[]> {
  const namespace = type.name;

  if (!liveCollections.has(namespace)) {
    const collection = ref<T[]>([]) as Ref<T[]>;

    PreferencesDB.getAll(type).then((records) => {
      collection.value = records.sort((a, b) =>
        sortNewestFirst ? b.createdAt - a.createdAt : a.createdAt - b.createdAt
      ) as T[];
    });

    liveCollections.set(namespace, collection);
  }

  return liveCollections.get(namespace) as Ref<T[]>;
}

export function usePreferencesItem<T extends DBRecord>(
  type: Constructor<T>,
  id: string
): Ref<T | undefined> {
  const key = getItemKey(type.name, id);

  if (!liveItems.has(key)) {
    const item = ref<T | undefined>(undefined) as Ref<T | undefined>;

    PreferencesDB.get(type.name, id).then((record) => {
      if (record) {
        const instance = new type();
        Object.assign(instance, record);
        item.value = instance;
      }
    });

    liveItems.set(key, item);
  }

  return liveItems.get(key) as Ref<T | undefined>;
}

export async function saveAndSync<T extends DBRecord>(
  type: Constructor<T>,
  record: T,
  backupAfterwards: boolean = true,
  sortNewestFirst = false
): Promise<void> {
  record.lastModified = Date.now();

  // Special handling for Profile to ensure Firebase is updated directly
  if (type.name === 'Profile' && record.id === '0') {
    // Profile is handled by FirebaseDB.ts, so we don't need to do anything special here
    // The local storage is still updated for offline support
  }

  // Save to local storage
  await PreferencesDB.save(type, record);

  // Update reactive references
  const namespace = type.name;
  const collection = liveCollections.get(namespace) as Ref<T[]> | undefined;
  if (collection) {
    const index = collection.value.findIndex((item) => item.id === record.id);
    if (index !== -1) {
      collection.value[index] = record;
    } else {
      collection.value.push(record);
    }

    collection.value = [...collection.value].sort((a, b) =>
      sortNewestFirst ? b.createdAt - a.createdAt : a.createdAt - b.createdAt
    );
  }

  const itemKey = getItemKey(namespace, record.id);
  const liveItem = liveItems.get(itemKey) as Ref<T> | undefined;
  if (liveItem) {
    liveItem.value = record;
  }

  // Sync with Firebase if needed
  if (backupAfterwards) {
    await restoreOrBackup();
  }
}

export const profile: Ref<Profile | undefined> = usePreferencesItem(Profile, '0');
if (profile.value == undefined) {
  profile.value = new Profile();
  profile.value.lastModified = 0;
}

export async function deleteAndSync<T extends DBRecord>(
  type: Constructor<T>,
  id: string,
  backupAfterwards: boolean = true
): Promise<void> {
  // Delete from local storage
  await PreferencesDB.delete(type.name, id);

  // Update reactive references
  const collection = liveCollections.get(type.name) as Ref<T[]> | undefined;
  if (collection) {
    collection.value = collection.value.filter((item) => item.id !== id);
  }

  // Update profile's deletedKeys
  if (profile.value != undefined) {
    profile.value.deletedKeys[id] = Date.now();

    // Update Firebase directly if user is logged in
    const user = useCurrentUser();
    if (user.value) {
      const db = getFirestore();
      const profileRef = doc(db, `users/${user.value.uid}/profile/default`);

      // Use updateDoc to only update the deletedKeys field
      await updateDoc(profileRef, {
        [`deletedKeys.${id}`]: Date.now(),
        lastModified: Date.now(),
      }).catch((error) => {
        console.error('Error updating deletedKeys in Firebase:', error);
      });
    }

    // Also update local storage for offline support
    await saveAndSync(Profile, profile.value, false);
  }

  // Sync with Firebase if needed
  if (backupAfterwards) {
    await restoreOrBackup();
  }
}
